import { Node, mergeAttributes } from '@tiptap/core';
import type { Command } from '@tiptap/core';

// Decklist card interface for storage
export interface DecklistCardEntry {
  quantity: number;
  name: string;
  scryfallId?: string;
  setCode?: string;
}

// Decklist attributes stored in the node
export interface DecklistAttrs {
  title: string;
  author: string;
  format: string;
  mainboard: DecklistCardEntry[];
  sideboard: DecklistCardEntry[];
}

// Declare module augmentation for custom commands
declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    emphasisBlock: {
      setEmphasisBlock: () => ReturnType;
      toggleEmphasisBlock: () => ReturnType;
    };
    calloutBox: {
      setCalloutBox: () => ReturnType;
      toggleCalloutBox: () => ReturnType;
    };
    button: {
      setButton: (options: { href: string; text: string; target?: string }) => ReturnType;
    };
    decklist: {
      setDecklist: (options: DecklistAttrs) => ReturnType;
    };
  }
}

// Emphasis Block Extension (purple left border highlight)
export const EmphasisBlock = Node.create({
  name: 'emphasisBlock',

  group: 'block',

  content: 'block+',

  defining: true,

  addAttributes() {
    return {};
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="emphasis-block"]',
      },
      {
        tag: 'div.emphasis',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'emphasis-block',
        class: 'emphasis',
      }),
      0,
    ];
  },

  addCommands() {
    return {
      setEmphasisBlock:
        (): Command =>
        ({ commands }: any) => {
          return commands.wrapIn(this.name);
        },
      toggleEmphasisBlock:
        (): Command =>
        ({ commands }: any) => {
          return commands.toggleWrap(this.name);
        },
    };
  },
});

// Callout Box Extension (purple background info box)
export const CalloutBox = Node.create({
  name: 'calloutBox',

  group: 'block',

  content: 'block+',

  defining: true,

  addAttributes() {
    return {};
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="callout-box"]',
      },
      {
        tag: 'div.callout',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'callout-box',
        class: 'callout',
      }),
      0,
    ];
  },

  addCommands() {
    return {
      setCalloutBox:
        (): Command =>
        ({ commands }: any) => {
          return commands.wrapIn(this.name);
        },
      toggleCalloutBox:
        (): Command =>
        ({ commands }: any) => {
          return commands.toggleWrap(this.name);
        },
    };
  },
});

// Button Extension (styled link button)
export const ButtonNode = Node.create({
  name: 'button',

  group: 'inline',

  inline: true,

  atom: true,

  addAttributes() {
    return {
      href: {
        default: null,
      },
      text: {
        default: 'Button',
      },
      target: {
        default: '_self',
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'a[data-type="button"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'a',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'button',
        class: 'article-button',
        href: HTMLAttributes.href,
        target: HTMLAttributes.target,
      }),
      HTMLAttributes.text,
    ];
  },

  addCommands() {
    return {
      setButton:
        (options: { href: string; text: string; target?: string }): Command =>
        ({ commands }: any) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              href: options.href,
              text: options.text,
              target: options.target || '_self',
            },
          });
        },
    };
  },
});

// Decklist Extension (embedded MTG decklist)
export const DecklistNode = Node.create({
  name: 'decklist',

  group: 'block',

  atom: true, // Non-editable, click to edit via modal

  draggable: true, // Allow repositioning in editor

  addAttributes() {
    return {
      title: {
        default: 'Untitled Deck',
      },
      author: {
        default: '',
      },
      format: {
        default: 'Unknown Format',
      },
      mainboard: {
        default: [],
        parseHTML: (element) => {
          const data = element.getAttribute('data-mainboard');
          try {
            return data ? JSON.parse(data) : [];
          } catch {
            return [];
          }
        },
        renderHTML: (attributes) => {
          return {
            'data-mainboard': JSON.stringify(attributes.mainboard || []),
          };
        },
      },
      sideboard: {
        default: [],
        parseHTML: (element) => {
          const data = element.getAttribute('data-sideboard');
          try {
            return data ? JSON.parse(data) : [];
          } catch {
            return [];
          }
        },
        renderHTML: (attributes) => {
          return {
            'data-sideboard': JSON.stringify(attributes.sideboard || []),
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="decklist"]',
        getAttrs: (dom) => {
          const element = dom as HTMLElement;
          return {
            title: element.getAttribute('data-title') || 'Untitled Deck',
            author: element.getAttribute('data-author') || '',
            format: element.getAttribute('data-format') || 'Unknown Format',
            // mainboard and sideboard are parsed via attribute parseHTML above
          };
        },
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const { title, author, format, mainboard, sideboard } = node.attrs;

    // Calculate card counts for preview
    const mainboardCount = (mainboard || []).reduce(
      (sum: number, card: DecklistCardEntry) => sum + (card.quantity || 0),
      0
    );
    const sideboardCount = (sideboard || []).reduce(
      (sum: number, card: DecklistCardEntry) => sum + (card.quantity || 0),
      0
    );

    // Build the preview HTML for the editor
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'decklist',
        'data-title': title,
        'data-author': author,
        'data-format': format,
        class: 'decklist-block',
      }),
      [
        'div',
        { class: 'decklist-preview' },
        [
          'div',
          { class: 'decklist-preview-header' },
          [
            'span',
            { class: 'decklist-preview-icon' },
            '📋',
          ],
          [
            'span',
            { class: 'decklist-preview-title' },
            title || 'Untitled Deck',
          ],
          [
            'span',
            { class: 'decklist-preview-format' },
            format || 'Unknown Format',
          ],
        ],
        [
          'div',
          { class: 'decklist-preview-meta' },
          `${author ? `by ${author} • ` : ''}${mainboardCount} cards${sideboardCount > 0 ? ` • ${sideboardCount} sideboard` : ''}`,
        ],
        [
          'div',
          { class: 'decklist-preview-hint' },
          'Click to edit decklist',
        ],
      ],
    ];
  },

  addCommands() {
    return {
      setDecklist:
        (options: DecklistAttrs): Command =>
        ({ commands }: any) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              title: options.title || 'Untitled Deck',
              author: options.author || '',
              format: options.format || 'Unknown Format',
              mainboard: options.mainboard || [],
              sideboard: options.sideboard || [],
            },
          });
        },
    };
  },
});
